import { useRef, useState } from "react";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

export default function HazardAddDialog({ categorys = [], checkDuplicate, onConfirm, onCancel }) {
  const [data, setData] = useState({ danger_category: "", danger_cause: "", danger_description: "" });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const pending = useRef(false);
  const categories = [...new Set(categorys.map((item) => item.danger_category_filter).filter(Boolean))];
  const causes = [...new Set(categorys.filter((item) => item.danger_category_filter === data.danger_category)
    .flatMap((item) => item.danger_cause_filters || []).map((item) => item.danger_cause).filter(Boolean))];
  const changeData = (changes) => {
    setData((current) => ({ ...current, ...changes }));
    setError((current) => current?.kind === "save" ? current : null);
  };
  const submit = async (event) => {
    event.preventDefault();
    if (pending.current) return;
    const payload = { ...data, danger_description: data.danger_description.trim() };
    if (!categories.includes(payload.danger_category) || !causes.includes(payload.danger_cause) || !payload.danger_description) {
      setError({ kind: "validation", message: "위험분류, 위험원인, 발생상황 및 결과를 모두 입력하세요." });
      return;
    }
    pending.current = true;
    setSaving(true);
    setError(null);
    try {
      if (await checkDuplicate(payload)) {
        setError({ kind: "duplicate", message: "동일한 유해·위험요인이 이미 등록되어 있습니다." });
        return;
      }
      await onConfirm(payload);
    } catch {
      setError({ kind: "save", message: "저장하지 못했습니다. 입력 내용은 유지됩니다. 다시 시도하세요." });
    } finally { pending.current = false; setSaving(false); }
  };
  return <Dialog open fullWidth maxWidth="sm" onClose={() => !pending.current && onCancel()}
    aria-labelledby="hazard-add-title" PaperProps={{ sx: { m: { xs: 1, sm: 3 }, width: { xs: "calc(100% - 16px)", sm: "100%" }, maxHeight: "calc(100dvh - 32px)" } }}>
    <Box component="form" aria-label="유해·위험요인 입력" noValidate onSubmit={submit} sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      <DialogTitle id="hazard-add-title">유해·위험요인 추가</DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
          <TextField select required fullWidth label="위험분류" value={data.danger_category} disabled={saving}
            onChange={(event) => changeData({ danger_category: event.target.value, danger_cause: "" })}>
            {categories.map((category) => <MenuItem key={category} value={category} sx={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{category}</MenuItem>)}
          </TextField>
          <TextField select required fullWidth label="위험원인" value={data.danger_cause} disabled={saving || !data.danger_category}
            onChange={(event) => changeData({ danger_cause: event.target.value })}>
            {causes.map((cause) => <MenuItem key={cause} value={cause} sx={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>{cause}</MenuItem>)}
          </TextField>
          <TextField required fullWidth multiline minRows={4} maxRows={10} label="발생상황 및 결과"
            value={data.danger_description} disabled={saving} sx={{ gridColumn: "1 / -1", minWidth: 0 }}
            onChange={(event) => changeData({ danger_description: event.target.value })} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button disabled={saving} onClick={onCancel}>취소</Button>
        <Button type="submit" variant="contained" startIcon={<AddIcon />} disabled={saving}>{saving ? "저장 중" : "추가"}</Button>
      </DialogActions>
    </Box>
  </Dialog>;
}
